
import { defineComponent, ref } from 'vue'
import ScrollParallax from 'vue3-parallax/src/components/ScrollParallax.vue'
import axios from 'axios'

import BackgroundImage from '@/components/Common/BackgroundImage.vue'
import Quote from '@/components/Common/Quote.vue'
import Button from '@/components/Common/Button.vue'
import Animate from '@/components/Common/Animate.vue'

interface FormData {
    name: string
    company: string
    email: string
    phone: string
    msg: string
}

export default defineComponent({
    name: 'Services',
    components: {
        ScrollParallax,
        BackgroundImage,
        Quote,
        Button,
        Animate,
    },
    setup() {
        const formData = ref({
            name: '',
            company: '',
            email: '',
            phone: '',
            message: '',
        })

        const isSending = ref(false)
        const isSendingSuccess = ref(false)
        const isSendingError = ref(false)
        const isValidationError = ref(false)

        const handleFormSubmit = async () => {
            if (isSending.value) return

            if (
                formData.value.name === '' ||
                formData.value.email === '' ||
                formData.value.message === ''
            ) {
                isValidationError.value = true
                return
            } else {
                isValidationError.value = false
            }

            const data: FormData = {
                name: formData.value.name,
                company: formData.value.company,
                email: formData.value.email,
                phone: formData.value.phone,
                msg: formData.value.message,
            }

            try {
                const result = await sendEmail(data)
                if (!result) throw 'Error posting email data'
                isSending.value = false

                // Show success message and remove after 3 seconds
                isSendingSuccess.value = true
                setTimeout(() => {
                    isSendingSuccess.value = false
                }, 3000)

                // Clear form input data
                formData.value = {
                    name: '',
                    company: '',
                    email: '',
                    phone: '',
                    message: '',
                }

                // Clear input active classes
                removeInputActiveClasses()
            } catch (error) {
                isSending.value = false

                // Show error message and remove after 5 seconds
                isSendingError.value = true
                setTimeout(() => {
                    isSendingError.value = false
                }, 5000)

                // eslint-disable-next-line
                console.log(error)
            }
        }

        const handleInputFocus = (e: Event) => {
            if (!e.target) return
            const target = e.target as HTMLInputElement
            target.nextElementSibling?.classList.add('input-active')
        }

        const handleInputBlur = (e: Event) => {
            if (!e.target) return
            const target = e.target as HTMLInputElement
            if (target.value === '') {
                target.nextElementSibling?.classList.remove('input-active')
            }
        }

        const removeInputActiveClasses = () => {
            const group = document.querySelectorAll(
                '.contact__content-contact-form--inputs-group'
            )
            group.forEach((el) => {
                el.firstElementChild?.nextElementSibling?.classList.remove(
                    'input-active'
                )
            })
        }

        const sendEmail = async (data: FormData) => {
            isSending.value = true
            const url =
                process.env.NODE_ENV === 'production'
                    ? 'https://node.retailonly.se'
                    : 'http://localhost:5500'

            const email = await axios.post(
                `${url}/sendemail`,
                JSON.stringify(data),
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            )

            if (email.status !== 200) {
                throw new Error(email.statusText || 'Error sending email')
            }

            return email
        }

        return {
            handleInputFocus,
            handleInputBlur,
            formData,
            isSending,
            isSendingSuccess,
            isSendingError,
            handleFormSubmit,
            isValidationError,
        }
    },
})
